import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Eyebrow } from '@/components/generals/eyebrow'
import { AnimatedHeading } from '@/components/motion/animated-heading'
import { Reveal } from '@/components/motion/reveal'

interface SectionHeadingProps {
    eyebrow?: string
    title: string
    description?: ReactNode
    action?: ReactNode
    tone?: 'dark' | 'light'
    layout?: 'stack' | 'split' | 'center'
    className?: string
    titleClassName?: string
    id?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    action,
    tone = 'dark',
    layout = 'split',
    className,
    titleClassName,
    id
}): ReactNode => {
    const onDark = tone === 'light'

    const heading = (
        <AnimatedHeading
            id={id}
            className={cn(
                'mt-5 max-w-[18ch] text-h2 font-semibold text-balance',
                onDark ? 'text-cream' : 'text-ink',
                layout === 'center' && 'mx-auto',
                titleClassName
            )}
        >
            {title}
        </AnimatedHeading>
    )

    const body = (description || action) && (
        <Reveal
            selector=':scope > *'
            className={cn(
                'flex flex-col gap-6',
                layout === 'center' && 'items-center',
                layout === 'split' && 'lg:items-start'
            )}
        >
            {description && (
                <div
                    className={cn(
                        'max-w-xl text-lead text-pretty',
                        onDark ? 'text-cream/65' : 'text-slate',
                        layout === 'center' && 'mx-auto'
                    )}
                >
                    {description}
                </div>
            )}

            {action && <div>{action}</div>}
        </Reveal>
    )

    if (layout === 'split') {
        return (
            <div className={cn('grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-10', className)}>
                <div className='lg:col-span-7'>
                    {eyebrow && <Eyebrow tone={tone}>{eyebrow}</Eyebrow>}
                    {heading}
                </div>

                <div className='lg:col-span-5 lg:pb-2'>
                    {body}
                </div>
            </div>
        )
    }

    return (
        <div className={cn('flex flex-col gap-6', layout === 'center' && 'items-center text-center', className)}>
            <div>
                {eyebrow && <Eyebrow tone={tone}>{eyebrow}</Eyebrow>}
                {heading}
            </div>

            {body}
        </div>
    )
}
